import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { Dropdownlist } from '@/components/Dropdownlist';
import { ConfirmModal } from '@/components/ConfirmModal';

// ---------------------------------------------------------------------------
// Global declarations
// ---------------------------------------------------------------------------

import moment from 'moment-timezone';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FieldInfo {
  label: string;
  field: string;
  type: 'text' | 'email' | 'dropdown' | 'timezone' | 'time-hour' | 'time';
}

interface DropdownOption {
  code: string | null;
  value: string;
}

interface TimeOption {
  value: string;
  display: string;
  searchText: string;
}

interface EditingField {
  uuid: string;
  field: string;
}

interface DeleteConfirmState {
  open: boolean;
  uuid: string | null;
  name: string;
}

export interface ClientNotificationsGridProps {
  notifications: any[];
  title?: string;
  isReadOnly?: boolean;
  onChange?: (newNotifications: any[]) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FIELDS: FieldInfo[] = [
  { label: 'Name', field: 'notifiedName', type: 'text' },
  { label: 'Email', field: 'notifiedEmail', type: 'email' },
  { label: 'Frequency', field: 'activityReportNotificationFrequency', type: 'dropdown' },
  { label: 'Email When No Data', field: 'emailActivityReportWhenNoData', type: 'dropdown' },
  { label: 'Email on Case Report Approval', field: 'receiveReportApprovedNotification', type: 'dropdown' },
  { label: 'Email on Call Closed', field: 'receiveCallCardClosedNotification', type: 'dropdown' },
  { label: 'Notification Time Zone', field: 'notificationTimeZoneId', type: 'timezone' },
  { label: 'Notification Time', field: 'notificationTimeLocal', type: 'time-hour' },
  { label: 'Activity Report Start Time', field: 'activityReportStartTimeLocal', type: 'time' },
  { label: 'Activity Report End Time', field: 'activityReportEndTimeLocal', type: 'time' },
];

const EDITABLE_FIELDS = [
  'notifiedName', 'notifiedEmail', 'activityReportNotificationFrequency',
  'emailActivityReportWhenNoData', 'receiveReportApprovedNotification',
  'receiveCallCardClosedNotification', 'notificationTimeZoneId',
  'notificationTimeLocal', 'activityReportStartTimeLocal', 'activityReportEndTimeLocal',
];

const FREQUENCY_OPTIONS: DropdownOption[] = [
  { code: 'd', value: 'Daily' },
  { code: 'w', value: 'Weekly' },
  { code: 'm', value: 'Monthly' },
  { code: null, value: 'None' },
];

const BOOLEAN_OPTIONS_EMAIL_WHEN_NO_DATA: DropdownOption[] = [
  { code: 'true', value: 'Always Email' },
  { code: 'false', value: 'Email Only When Data' },
];

const BOOLEAN_OPTIONS_REPORT_APPROVED: DropdownOption[] = [
  { code: 'true', value: 'When Approved' },
  { code: 'false', value: 'Never' },
];

const BOOLEAN_OPTIONS_CALL_CARD_CLOSED: DropdownOption[] = [
  { code: 'closed-detailed', value: 'When Closed (Detailed Email)' },
  { code: 'closed', value: 'When Closed' },
  { code: 'false', value: 'Never' },
];

const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const PERIODS: ('AM' | 'PM')[] = ['AM', 'PM'];

const HEADER_WIDTHS: Record<string, string> = {
  notifiedName: '11%',
  notifiedEmail: '14%',
  activityReportNotificationFrequency: '8%',
  emailActivityReportWhenNoData: '10%',
  receiveReportApprovedNotification: '10%',
  receiveCallCardClosedNotification: '10%',
  notificationTimeZoneId: '11%',
  notificationTimeLocal: '8%',
  activityReportStartTimeLocal: '9%',
  activityReportEndTimeLocal: '9%',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pad(num: number): string {
  return num < 10 ? '0' + num : num.toString();
}

function convertTo12Hour(time24: string): { hour: number; minute: number; period: 'AM' | 'PM' } {
  const [hours, minutes] = time24.split(':').map(Number);
  const period: 'AM' | 'PM' = hours >= 12 ? 'PM' : 'AM';
  const hour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return { hour, minute: minutes, period };
}

function convertTo24Hour(hour: number, minute: number, period: 'AM' | 'PM'): string {
  let hours24 = hour;
  if (period === 'AM' && hour === 12) {
    hours24 = 0;
  } else if (period === 'PM' && hour !== 12) {
    hours24 = hour + 12;
  }
  return `${pad(hours24)}:${pad(minute)}`;
}

function generateTimeOptions(isHourOnly: boolean): TimeOption[] {
  const options: TimeOption[] = [];
  if (isHourOnly) {
    for (let h = 0; h < 24; h++) {
      const hour = pad(h);
      options.push({
        value: `${hour}:00`,
        display: `${hour}:00`,
        searchText: `${hour}:00 ${h}`,
      });
    }
  } else {
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m++) {
        const hour = pad(h);
        const minute = pad(m);
        options.push({
          value: `${hour}:${minute}`,
          display: `${hour}:${minute}`,
          searchText: `${hour}:${minute} ${h}:${m}`,
        });
      }
    }
  }
  return options;
}

function formatTime(time: any): string {
  if (time == null || time === '') return '\u2014';
  if (typeof time === 'number') {
    const m = moment().startOf('day').add(time, 'milliseconds');
    return m.format('HH:mm');
  }
  return moment(time).format('HH:mm');
}

function getDropdownOptions(field: string): DropdownOption[] {
  switch (field) {
    case 'activityReportNotificationFrequency':
      return FREQUENCY_OPTIONS;
    case 'emailActivityReportWhenNoData':
      return BOOLEAN_OPTIONS_EMAIL_WHEN_NO_DATA;
    case 'receiveReportApprovedNotification':
      return BOOLEAN_OPTIONS_REPORT_APPROVED;
    case 'receiveCallCardClosedNotification':
      return BOOLEAN_OPTIONS_CALL_CARD_CLOSED;
    default:
      return [];
  }
}

function getDisplayValue(notification: any, field: string): string {
  const value = notification[field];
  const fieldInfo = FIELDS.find(f => f.field === field);
  if (value == null || value === '') return '\u2014';
  if (fieldInfo?.type === 'dropdown') {
    return value.value != null ? String(value.value) : '\u2014';
  } else if (fieldInfo?.type === 'time' || fieldInfo?.type === 'time-hour') {
    return formatTime(value);
  } else if (fieldInfo?.type === 'timezone') {
    return value;
  }
  return String(value);
}

function parseTimeValue(currentValue: any): string {
  if (currentValue) {
    let m;
    if (typeof currentValue === 'number') {
      m = moment().startOf('day').add(currentValue, 'milliseconds');
    } else {
      m = moment(currentValue);
    }
    return m.format('HH:mm');
  }
  return '00:00';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ClientNotificationsGrid({
  notifications = [],
  title = 'Automated Notifications',
  isReadOnly = false,
  onChange,
}: ClientNotificationsGridProps) {
  const { theme } = useTheme();
  const rootRef = useRef<HTMLDivElement>(null);

  // ---- State ----
  const [editingField, setEditingField] = useState<EditingField | null>(null);
  const [editValue, setEditValue] = useState<any>('');
  const [isMobile, setIsMobile] = useState(false);
  const [isTabbing, setIsTabbing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isJustStartingEdit, setIsJustStartingEdit] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
    open: false,
    uuid: null,
    name: '',
  });

  // Time picker state
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [timePickerMode, setTimePickerMode] = useState<'hour-only' | 'full'>('hour-only');
  const [timePickerOptions, setTimePickerOptions] = useState<TimeOption[]>([]);
  const [timePickerActiveIndex, setTimePickerActiveIndex] = useState(-1);
  const [timePickerPortalStyle, setTimePickerPortalStyle] = useState<CSSProperties>({});
  const [timePickerJustOpened, setTimePickerJustOpened] = useState(false);

  // Full time picker 12-hour state
  const [selectedHour, setSelectedHour] = useState(12);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState<'AM' | 'PM'>('AM');

  // Time zones
  const [timeZones, setTimeZones] = useState<any[]>([]);

  // Flag for auto-opening dropdowns/time pickers after the control renders
  const pendingAutoOpenRef = useRef<string | null>(null);

  // ---- Refs for latest values in event handlers ----
  const editingFieldRef = useRef(editingField);
  const editValueRef = useRef(editValue);
  const isSavingRef = useRef(isSaving);
  const isJustStartingEditRef = useRef(isJustStartingEdit);
  const notificationsRef = useRef(notifications);
  const onChangeRef = useRef(onChange);
  const timePickerOpenRef = useRef(timePickerOpen);
  const timePickerJustOpenedRef = useRef(timePickerJustOpened);
  const timePickerControlRef = useRef<HTMLElement | null>(null);
  const selectedHourRef = useRef(selectedHour);
  const selectedMinuteRef = useRef(selectedMinute);
  const selectedPeriodRef = useRef(selectedPeriod);

  // Sync refs
  useEffect(() => { editingFieldRef.current = editingField; }, [editingField]);
  useEffect(() => { editValueRef.current = editValue; }, [editValue]);
  useEffect(() => { isSavingRef.current = isSaving; }, [isSaving]);
  useEffect(() => { isJustStartingEditRef.current = isJustStartingEdit; }, [isJustStartingEdit]);
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { timePickerOpenRef.current = timePickerOpen; }, [timePickerOpen]);
  useEffect(() => { timePickerJustOpenedRef.current = timePickerJustOpened; }, [timePickerJustOpened]);
  useEffect(() => { selectedHourRef.current = selectedHour; }, [selectedHour]);
  useEffect(() => { selectedMinuteRef.current = selectedMinute; }, [selectedMinute]);
  useEffect(() => { selectedPeriodRef.current = selectedPeriod; }, [selectedPeriod]);

  // ---- Emit helpers ----
  const emitReplace = useCallback((newArr: any[]) => {
    onChangeRef.current?.(newArr);
  }, []);

  const emitMap = useCallback((fn: (n: any) => any) => {
    emitReplace(notificationsRef.current.map(fn));
  }, [emitReplace]);

  // ---- Mobile check (debounced) ----

  // ---- Load time zones ----
  useEffect(() => {
    if (typeof (window as any).SelectableTimeZones !== 'undefined') {
      setTimeZones((window as any).SelectableTimeZones);
    }
  }, []);

  // ---- Time picker display ----
  const getTimePickerDisplayValue = useCallback((): string => {
    const ev = editValueRef.current;
    if (!ev) return '';
    if (timePickerMode === 'hour-only' || !editingFieldRef.current) {
      return ev;
    }
    const time12 = convertTo12Hour(ev);
    return `${time12.hour}:${pad(time12.minute)} ${time12.period}`;
  }, [timePickerMode]);

  // ---- Position time picker portal ----
  const positionTimePickerPortal = useCallback((controlElement: HTMLElement) => {
    const rect = controlElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownMaxHeight = 300;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;
    const mode = timePickerMode;
    const dropdownWidth = mode === 'full' ? 280 : rect.width;

    let leftPos = rect.left;
    const rightEdge = leftPos + dropdownWidth;
    const padding = 10;

    if (rightEdge > viewportWidth - padding) {
      leftPos = viewportWidth - dropdownWidth - padding;
      if (leftPos < padding) {
        leftPos = Math.max(padding, rect.right - dropdownWidth);
      }
    }
    leftPos = Math.max(padding, leftPos);

    const style: CSSProperties = {
      position: 'fixed',
      left: leftPos + 'px',
      width: dropdownWidth + 'px',
      zIndex: 10000,
      visibility: 'visible',
    };

    if (openAbove) {
      style.bottom = (viewportHeight - rect.top + 2) + 'px';
      style.maxHeight = Math.min(dropdownMaxHeight, spaceAbove - 10) + 'px';
    } else {
      style.top = (rect.bottom + 2) + 'px';
      style.maxHeight = Math.min(dropdownMaxHeight, spaceBelow - 10) + 'px';
    }

    setTimePickerPortalStyle(style);
  }, [timePickerMode]);

  const updateTimePickerPosition = useCallback(() => {
    if (timePickerOpenRef.current && timePickerControlRef.current) {
      positionTimePickerPortal(timePickerControlRef.current);
    }
  }, [positionTimePickerPortal]);

  // ---- Close time picker ----
  const closeTimePicker = useCallback(() => {
    setTimePickerOpen(false);
    setTimePickerActiveIndex(-1);
    timePickerControlRef.current = null;
    setTimePickerPortalStyle({});
    setTimePickerJustOpened(false);
  }, []);

  // ---- Save edit ----
  const saveEdit = useCallback(() => {
    if (!editingFieldRef.current || isSavingRef.current) return;
    if (isJustStartingEditRef.current) return;

    isSavingRef.current = true;
    setIsSaving(true);

    const { uuid, field } = editingFieldRef.current;
    const fieldInfo = FIELDS.find(f => f.field === field);
    const currentEditValue = editValueRef.current;

    if (fieldInfo?.type === 'dropdown') {
      const options = getDropdownOptions(field);
      const selected = options.find(opt => opt.code === currentEditValue);
      emitMap((n: any) => (n.uuid === uuid ? { ...n, [field]: selected || null } : n));
    } else if (fieldInfo?.type === 'time' || fieldInfo?.type === 'time-hour') {
      const [hours, minutes] = currentEditValue.split(':').map(Number);
      const date = moment().hours(hours).minutes(minutes).seconds(0).milliseconds(0).toDate();
      emitMap((n: any) => (n.uuid === uuid ? { ...n, [field]: date } : n));
    } else if (fieldInfo?.type === 'timezone') {
      emitMap((n: any) => (n.uuid === uuid ? { ...n, [field]: currentEditValue } : n));
    } else {
      emitMap((n: any) => (n.uuid === uuid ? { ...n, [field]: currentEditValue } : n));
    }

    editingFieldRef.current = null;
    editValueRef.current = '';
    setEditingField(null);
    setEditValue('');

    setTimeout(() => {
      isSavingRef.current = false;
      setIsSaving(false);
    }, 100);
  }, [emitMap]);

  // ---- Cancel edit ----
  const cancelEdit = useCallback(() => {
    closeTimePicker();
    setEditingField(null);
    setEditValue('');
    editingFieldRef.current = null;
    editValueRef.current = '';
  }, [closeTimePicker]);

  // ---- Scroll to active item (hour-only mode) ----
  const scrollToActiveItem = useCallback((index?: number) => {
    requestAnimationFrame(() => {
      const portal = document.querySelector('.time-picker-portal');
      if (!portal) return;
      const list = portal.querySelector('.time-picker-list-hour-only');
      const items = portal.querySelectorAll('.time-picker-item:not(.time-picker-item--empty)');
      const targetIndex = index !== undefined ? index : -1;
      const item = items[targetIndex >= 0 ? targetIndex : 0];
      if (list && item) {
        (item as HTMLElement).scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });
  }, []);

  // ---- Scroll to current time (full mode) ----
  const scrollToCurrentTime = useCallback(() => {
    requestAnimationFrame(() => {
      const portal = document.querySelector('.time-picker-portal');
      if (!portal) return;

      const hourItems = portal.querySelectorAll('.time-picker-column-hour .column-item');
      const hourIndex = HOURS_12.indexOf(selectedHourRef.current);
      if (hourItems[hourIndex]) {
        (hourItems[hourIndex] as HTMLElement).scrollIntoView({ block: 'center' });
      }

      const minuteItems = portal.querySelectorAll('.time-picker-column-minute .column-item');
      if (minuteItems[selectedMinuteRef.current]) {
        (minuteItems[selectedMinuteRef.current] as HTMLElement).scrollIntoView({ block: 'center' });
      }
    });
  }, []);

  // ---- Select time option (hour-only) ----
  const selectTimeOption = useCallback((option: TimeOption) => {
    editValueRef.current = option.value;
    setEditValue(option.value);
    closeTimePicker();
    // Save after a tick so state is updated
    setTimeout(() => {
      // Inline save since we need to use the option value directly
      if (!editingFieldRef.current || isSavingRef.current) return;

      isSavingRef.current = true;
      setIsSaving(true);

      const { uuid, field } = editingFieldRef.current;
      const [hours, minutes] = option.value.split(':').map(Number);
      const date = moment().hours(hours).minutes(minutes).seconds(0).milliseconds(0).toDate();
      const updated = notificationsRef.current.map((n: any) =>
        n.uuid === uuid ? { ...n, [field]: date } : n
      );
      onChangeRef.current?.(updated);

      editingFieldRef.current = null;
      editValueRef.current = '';
      setEditingField(null);
      setEditValue('');

      setTimeout(() => {
        isSavingRef.current = false;
        setIsSaving(false);
      }, 100);
    }, 0);
  }, [closeTimePicker]);

  // ---- Update editValue from full picker ----
  const updateEditValueFromFullPicker = useCallback((hour: number, minute: number, period: 'AM' | 'PM') => {
    const val = convertTo24Hour(hour, minute, period);
    editValueRef.current = val;
    setEditValue(val);
  }, []);

  // ---- Select hour/minute/period ----
  const selectHour = useCallback((hour: number) => {
    setSelectedHour(hour);
    selectedHourRef.current = hour;
    updateEditValueFromFullPicker(hour, selectedMinuteRef.current, selectedPeriodRef.current);
  }, [updateEditValueFromFullPicker]);

  const selectMinute = useCallback((minute: number) => {
    setSelectedMinute(minute);
    selectedMinuteRef.current = minute;
    updateEditValueFromFullPicker(selectedHourRef.current, minute, selectedPeriodRef.current);
  }, [updateEditValueFromFullPicker]);

  const selectPeriod = useCallback((period: 'AM' | 'PM') => {
    setSelectedPeriod(period);
    selectedPeriodRef.current = period;
    updateEditValueFromFullPicker(selectedHourRef.current, selectedMinuteRef.current, period);
  }, [updateEditValueFromFullPicker]);

  // ---- Open time picker ----
  const openTimePicker = useCallback((field: string, event?: React.MouseEvent) => {
    const fieldInfo = FIELDS.find(f => f.field === field);
    const isHourOnly = fieldInfo?.type === 'time-hour';
    const mode = isHourOnly ? 'hour-only' : 'full';

    setTimePickerMode(mode);
    setTimePickerPortalStyle({ position: 'fixed', visibility: 'hidden' } as CSSProperties);
    setTimePickerOpen(true);
    setTimePickerJustOpened(true);
    timePickerOpenRef.current = true;
    timePickerJustOpenedRef.current = true;

    if (isHourOnly) {
      const opts = generateTimeOptions(true);
      setTimePickerOptions(opts);
      const activeIdx = opts.findIndex(opt => opt.value === editValueRef.current);
      setTimePickerActiveIndex(activeIdx);

      // Position and scroll after render
      requestAnimationFrame(() => {
        let control: HTMLElement | null = null;

        if (event && event.currentTarget) {
          control = event.currentTarget as HTMLElement;
          if (!control.classList.contains('time-picker-control')) {
            control = (event.currentTarget as HTMLElement).querySelector('.time-picker-control');
          }
        }

        if (!control && rootRef.current) {
          const allControls = rootRef.current.querySelectorAll('.time-picker-control');
          for (const ctrl of Array.from(allControls)) {
            const htmlCtrl = ctrl as HTMLElement;
            if (htmlCtrl.offsetWidth > 0 && htmlCtrl.offsetHeight > 0) {
              control = htmlCtrl;
              break;
            }
          }
        }

        if (control) {
          timePickerControlRef.current = control;
          // Re-calculate position with the correct mode
          const rect = control.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          const dropdownMaxHeight = 300;
          const spaceBelow = viewportHeight - rect.bottom;
          const spaceAbove = rect.top;
          const openAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;
          const dropdownWidth = rect.width; // hour-only uses control width

          let leftPos = rect.left;
          const rightEdge = leftPos + dropdownWidth;
          const paddingVal = 10;
          if (rightEdge > viewportWidth - paddingVal) {
            leftPos = viewportWidth - dropdownWidth - paddingVal;
            if (leftPos < paddingVal) {
              leftPos = Math.max(paddingVal, rect.right - dropdownWidth);
            }
          }
          leftPos = Math.max(paddingVal, leftPos);

          const style: CSSProperties = {
            position: 'fixed',
            left: leftPos + 'px',
            width: dropdownWidth + 'px',
            zIndex: 10000,
            visibility: 'visible',
          };

          if (openAbove) {
            style.bottom = (viewportHeight - rect.top + 2) + 'px';
            style.maxHeight = Math.min(dropdownMaxHeight, spaceAbove - 10) + 'px';
          } else {
            style.top = (rect.bottom + 2) + 'px';
            style.maxHeight = Math.min(dropdownMaxHeight, spaceBelow - 10) + 'px';
          }

          setTimePickerPortalStyle(style);
        }

        if (activeIdx >= 0) {
          scrollToActiveItem(activeIdx);
        }

        setTimeout(() => {
          setTimePickerJustOpened(false);
          timePickerJustOpenedRef.current = false;
        }, 100);
      });
    } else {
      // Full time picker mode
      const time12 = convertTo12Hour(editValueRef.current);
      setSelectedHour(time12.hour);
      setSelectedMinute(time12.minute);
      setSelectedPeriod(time12.period);
      selectedHourRef.current = time12.hour;
      selectedMinuteRef.current = time12.minute;
      selectedPeriodRef.current = time12.period;

      requestAnimationFrame(() => {
        let control: HTMLElement | null = null;

        if (event && event.currentTarget) {
          control = event.currentTarget as HTMLElement;
          if (!control.classList.contains('time-picker-control')) {
            control = (event.currentTarget as HTMLElement).querySelector('.time-picker-control');
          }
        }

        if (!control && rootRef.current) {
          const allControls = rootRef.current.querySelectorAll('.time-picker-control');
          for (const ctrl of Array.from(allControls)) {
            const htmlCtrl = ctrl as HTMLElement;
            if (htmlCtrl.offsetWidth > 0 && htmlCtrl.offsetHeight > 0) {
              control = htmlCtrl;
              break;
            }
          }
        }

        if (control) {
          timePickerControlRef.current = control;
          // Re-calculate position with the correct mode
          const rect = control.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          const dropdownMaxHeight = 300;
          const spaceBelow = viewportHeight - rect.bottom;
          const spaceAbove = rect.top;
          const openAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;
          const dropdownWidth = 280; // full mode uses 280px

          let leftPos = rect.left;
          const rightEdge = leftPos + dropdownWidth;
          const paddingVal = 10;
          if (rightEdge > viewportWidth - paddingVal) {
            leftPos = viewportWidth - dropdownWidth - paddingVal;
            if (leftPos < paddingVal) {
              leftPos = Math.max(paddingVal, rect.right - dropdownWidth);
            }
          }
          leftPos = Math.max(paddingVal, leftPos);

          const style: CSSProperties = {
            position: 'fixed',
            left: leftPos + 'px',
            width: dropdownWidth + 'px',
            zIndex: 10000,
            visibility: 'visible',
          };

          if (openAbove) {
            style.bottom = (viewportHeight - rect.top + 2) + 'px';
            style.maxHeight = Math.min(dropdownMaxHeight, spaceAbove - 10) + 'px';
          } else {
            style.top = (rect.bottom + 2) + 'px';
            style.maxHeight = Math.min(dropdownMaxHeight, spaceBelow - 10) + 'px';
          }

          setTimePickerPortalStyle(style);
        }

        scrollToCurrentTime();

        setTimeout(() => {
          setTimePickerJustOpened(false);
          timePickerJustOpenedRef.current = false;
        }, 100);
      });
    }
  }, [scrollToActiveItem, scrollToCurrentTime]);

  // ---- Focus input ----
  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      if (!rootRef.current) return;
      const editContainer = rootRef.current.querySelector('.edit-container, .edit-field-mobile');
      if (editContainer) {
        const input = editContainer.querySelector('.edit-input:not([type="hidden"])') as HTMLInputElement;
        if (input) {
          if (window.innerWidth <= 1024) {
            setTimeout(() => {
              input.focus();
              input.click();
            }, 150);
          } else {
            input.focus();
            if (input.type === 'text' || input.type === 'email') {
              input.select();
            }
          }
        }
      }
    });
  }, []);

  // ---- Start edit (desktop) ----
  const startEdit = useCallback((rowUuid: string, field: string, currentValue: any) => {
    if (isReadOnly) return;

    // Save any existing edit before starting a new one
    if (editingFieldRef.current && !isSavingRef.current) {
      if (timePickerOpenRef.current) {
        closeTimePicker();
      }
      saveEdit();
    }

    const newEditing = { uuid: rowUuid, field };
    editingFieldRef.current = newEditing;
    setEditingField(newEditing);

    const fieldInfo = FIELDS.find(f => f.field === field);
    if (!fieldInfo) return;

    if (fieldInfo.type === 'dropdown') {
      const val = currentValue?.code ?? null;
      editValueRef.current = val;
      setEditValue(val);
    } else if (fieldInfo.type === 'timezone') {
      const val = currentValue || moment.tz.guess();
      editValueRef.current = val;
      setEditValue(val);
    } else if (fieldInfo.type === 'time' || fieldInfo.type === 'time-hour') {
      const val = parseTimeValue(currentValue);
      editValueRef.current = val;
      setEditValue(val);
    } else {
      const val = currentValue || '';
      editValueRef.current = val;
      setEditValue(val);
    }

    if (fieldInfo.type === 'text' || fieldInfo.type === 'email') {
      focusInput();
    } else if (fieldInfo.type === 'dropdown' || fieldInfo.type === 'timezone' || fieldInfo.type === 'time' || fieldInfo.type === 'time-hour') {
      pendingAutoOpenRef.current = fieldInfo.type;
    }
  }, [isReadOnly, closeTimePicker, saveEdit, focusInput]);

  // ---- Start edit (mobile) ----
  const startEditMobile = useCallback((rowUuid: string, field: string, currentValue: any) => {
    if (isReadOnly) return;

    if (editingFieldRef.current && !isSavingRef.current) {
      if (timePickerOpenRef.current) {
        closeTimePicker();
      }
      saveEdit();
    }

    const fieldInfo = FIELDS.find(f => f.field === field);

    if (fieldInfo && (fieldInfo.type === 'text' || fieldInfo.type === 'email')) {
      setIsJustStartingEdit(true);
      isJustStartingEditRef.current = true;

      const newEditing = { uuid: rowUuid, field };
      editingFieldRef.current = newEditing;
      setEditingField(newEditing);
      const val = currentValue || '';
      editValueRef.current = val;
      setEditValue(val);

      setTimeout(() => {
        setIsJustStartingEdit(false);
        isJustStartingEditRef.current = false;
      }, 200);
    } else if (fieldInfo && (fieldInfo.type === 'dropdown' || fieldInfo.type === 'timezone')) {
      const newEditing = { uuid: rowUuid, field };
      editingFieldRef.current = newEditing;
      setEditingField(newEditing);

      if (fieldInfo.type === 'dropdown') {
        const val = currentValue?.code ?? null;
        editValueRef.current = val;
        setEditValue(val);
      } else {
        const val = currentValue || moment.tz.guess();
        editValueRef.current = val;
        setEditValue(val);
      }

      pendingAutoOpenRef.current = fieldInfo.type;
    } else if (fieldInfo && (fieldInfo.type === 'time' || fieldInfo.type === 'time-hour')) {
      const newEditing = { uuid: rowUuid, field };
      editingFieldRef.current = newEditing;
      setEditingField(newEditing);

      const val = parseTimeValue(currentValue);
      editValueRef.current = val;
      setEditValue(val);

      pendingAutoOpenRef.current = fieldInfo.type;
    } else {
      startEdit(rowUuid, field, currentValue);
    }
  }, [isReadOnly, closeTimePicker, saveEdit, startEdit]);

  // ---- Get next editable field (for tab navigation) ----
  const getNextEditableField = useCallback((currentUuid: string, currentField: string): { uuid: string; field: string } | null => {
    const currentNotificationIndex = notificationsRef.current.findIndex((n: any) => n.uuid === currentUuid);
    const currentFieldIndex = EDITABLE_FIELDS.indexOf(currentField);

    if (currentFieldIndex < EDITABLE_FIELDS.length - 1) {
      return { uuid: currentUuid, field: EDITABLE_FIELDS[currentFieldIndex + 1] };
    }

    if (currentNotificationIndex < notificationsRef.current.length - 1) {
      const nextNotification = notificationsRef.current[currentNotificationIndex + 1];
      return { uuid: nextNotification.uuid, field: EDITABLE_FIELDS[0] };
    }

    return null;
  }, []);

  // ---- Handle tab key ----
  const handleTabKey = useCallback((event: React.KeyboardEvent) => {
    if (!editingFieldRef.current) return;

    event.preventDefault();
    event.stopPropagation();

    setIsTabbing(true);
    const currentField = { ...editingFieldRef.current };

    saveEdit();

    setTimeout(() => {
      const next = getNextEditableField(currentField.uuid, currentField.field);
      if (next) {
        const notification = notificationsRef.current.find((n: any) => n.uuid === next.uuid);
        if (notification) {
          startEdit(next.uuid, next.field, notification[next.field]);
        }
      }
      setIsTabbing(false);
    }, 0);
  }, [saveEdit, getNextEditableField, startEdit]);

  // ---- Handle dropdown select ----
  const handleDropdownSelect = useCallback((field: string, selectedItem: any) => {
    if (selectedItem) {
      editValueRef.current = selectedItem.code ?? null;
      setEditValue(selectedItem.code ?? null);
    }
    setTimeout(() => {
      saveEdit();
    }, 0);
  }, [saveEdit]);

  // ---- Handle timezone select ----
  const handleTimezoneSelect = useCallback((selectedItem: any) => {
    if (selectedItem) {
      editValueRef.current = selectedItem.code;
      setEditValue(selectedItem.code);
    }
    setTimeout(() => {
      saveEdit();
    }, 0);
  }, [saveEdit]);

  // ---- Handle time picker keydown ----
  const handleTimePickerKeydown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setTimePickerActiveIndex(prev => {
        const newIndex = Math.min(prev + 1, timePickerOptions.length - 1);
        scrollToActiveItem(newIndex);
        return newIndex;
      });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setTimePickerActiveIndex(prev => {
        const newIndex = Math.max(prev - 1, 0);
        scrollToActiveItem(newIndex);
        return newIndex;
      });
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (timePickerActiveIndex >= 0 && timePickerOptions[timePickerActiveIndex]) {
        selectTimeOption(timePickerOptions[timePickerActiveIndex]);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closeTimePicker();
      cancelEdit();
    }
  }, [timePickerOptions, timePickerActiveIndex, scrollToActiveItem, selectTimeOption, closeTimePicker, cancelEdit]);

  // ---- Add notification ----
  const addNotification = useCallback(() => {
    if (isReadOnly) return;

    const momentTzGuess: string = moment.tz.guess();
    const defaultNotificationTime = moment().hours(6).minutes(0).seconds(0).milliseconds(0);
    const defaultStartAndEndTime = moment().hours(0).minutes(0).seconds(0).milliseconds(0);

    const newNotification = {
      uuid: getInsecureUUID(),
      id: null,
      notifiedName: '',
      notifiedEmail: '',
      activityReportNotificationFrequency: { code: 'd', value: 'Daily' },
      emailActivityReportWhenNoData: { code: 'false', value: 'Email Only When Data' },
      receiveReportApprovedNotification: { code: 'false', value: 'Never' },
      receiveCallCardClosedNotification: { code: 'false', value: 'Never' },
      notificationTimeLocal: defaultNotificationTime.toDate(),
      activityReportStartTimeLocal: defaultStartAndEndTime.toDate(),
      activityReportEndTimeLocal: defaultStartAndEndTime.toDate(),
      notificationTimeZoneId: momentTzGuess,
    };
    emitReplace([...notificationsRef.current, newNotification]);
    requestAnimationFrame(() => {
      if (rootRef.current) {
        scrollToLastMobileCard(rootRef.current);
      }
    });
  }, [isReadOnly, emitReplace]);

  // ---- Delete confirm ----
  const openDeleteConfirm = useCallback((uuid: string) => {
    if (isReadOnly) return;
    const row = notificationsRef.current.find((n: any) => n.uuid === uuid);
    setDeleteConfirm({
      open: true,
      uuid,
      name: row ? row.notifiedName || row.notifiedEmail : 'this notification',
    });
  }, [isReadOnly]);

  const confirmDelete = useCallback(() => {
    const uuid = deleteConfirm.uuid;
    if (!uuid) {
      setDeleteConfirm({ open: false, uuid: null, name: '' });
      return;
    }
    emitReplace(notificationsRef.current.filter((n: any) => n.uuid !== uuid));
    setDeleteConfirm({ open: false, uuid: null, name: '' });
  }, [deleteConfirm.uuid, emitReplace]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, uuid: null, name: '' });
  }, []);

  // ---- Is editing check ----
  const isEditing = useCallback((rowUuid: string, field: string): boolean => {
    return editingField?.uuid === rowUuid && editingField?.field === field;
  }, [editingField]);

  // ---- Auto-open dropdown/time picker after DOM commit ----
  useEffect(() => {
    const type = pendingAutoOpenRef.current;
    if (!type || !editingField) return;
    pendingAutoOpenRef.current = null;

    if (type === 'dropdown' || type === 'timezone') {
      // Click the Dropdownlist control to open it through its normal path
      requestAnimationFrame(() => {
        if (!rootRef.current) return;
        const control = rootRef.current.querySelector('.edit-container .kst-select__control');
        if (control) {
          (control as HTMLElement).click();
        }
      });
    } else if (type === 'time' || type === 'time-hour') {
      setTimeout(() => {
        openTimePicker(editingField.field);
      }, 50);
    }
  });

  // ---- Event listeners ----
  useEffect(() => {
    let mobileTimer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (mobileTimer) clearTimeout(mobileTimer);
      mobileTimer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkIfMobile);
    window.addEventListener('resize', updateTimePickerPosition);
    window.addEventListener('scroll', updateTimePickerPosition, true);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
      window.removeEventListener('resize', updateTimePickerPosition);
      window.removeEventListener('scroll', updateTimePickerPosition, true);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, [updateTimePickerPosition]);

  // Document click handler for time picker
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!timePickerOpenRef.current) return;
      if (timePickerJustOpenedRef.current) return;

      const target = event.target as HTMLElement;
      const portal = document.querySelector('.time-picker-portal');
      const control = timePickerControlRef.current;

      if (!target.closest('.time-picker-control') &&
        (!portal || !portal.contains(target)) &&
        (!control || !control.contains(target))) {
        saveEdit();
        closeTimePicker();
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [saveEdit, closeTimePicker]);

  // Document click handler for cell click-outside
  useEffect(() => {
    const handleCellClickOutside = (event: MouseEvent) => {
      if (!editingFieldRef.current) return;
      if (timePickerOpenRef.current) return;

      const target = event.target as HTMLElement;
      const isInsideEditContainer = target.closest('.edit-container, .edit-field-mobile');
      const isInsideDropdown = target.closest('.kst-select, .kst-select-dropdown, .kst-select-portal');

      if (!isInsideEditContainer && !isInsideDropdown) {
        saveEdit();
      }
    };

    document.addEventListener('click', handleCellClickOutside);
    return () => {
      document.removeEventListener('click', handleCellClickOutside);
    };
  }, [saveEdit]);

  // ---- Computed ----
  const filteredTimeOptions = useMemo(() => timePickerOptions, [timePickerOptions]);

  // ---- Render helpers ----

  const renderTextCell = (notification: any, field: string, inputType: string = 'text') => {
    if (isEditing(notification.uuid, field)) {
      return (
        <div className="edit-container">
          <input
            type={inputType}
            className="edit-input"
            value={editValue}
            onChange={(e) => { editValueRef.current = e.target.value; setEditValue(e.target.value); }}
            onBlur={() => saveEdit()}
            onKeyUp={(e) => {
              if (e.key === 'Enter') saveEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            onKeyDown={(e) => { if (e.key === 'Tab') handleTabKey(e); }}
            autoFocus
          />
        </div>
      );
    }
    return (
      <div
        className={`editable${isReadOnly ? ' read-only' : ''}`}
        onClick={(e) => { e.stopPropagation(); if (!isReadOnly) startEdit(notification.uuid, field, notification[field]); }}
      >
        {notification[field] || '\u2014'}
      </div>
    );
  };

  const renderDropdownCell = (notification: any, field: string) => {
    if (isEditing(notification.uuid, field)) {
      const options = getDropdownOptions(field);
      return (
        <div className="edit-container">
          <Dropdownlist
            value={editValue}
            items={options}
            labelKey="value"
            valueKey="code"
            placeholder="Select..."
            searchable={false}
            sortItems={false}
            onItemChange={(item) => handleDropdownSelect(field, item)}
            onCancel={() => saveEdit()}
          />
        </div>
      );
    }
    return (
      <div
        className={`editable${isReadOnly ? ' read-only' : ''}`}
        onClick={(e) => { e.stopPropagation(); if (!isReadOnly) startEdit(notification.uuid, field, notification[field]); }}
      >
        {getDisplayValue(notification, field)}
      </div>
    );
  };

  const renderTimezoneCell = (notification: any) => {
    if (isEditing(notification.uuid, 'notificationTimeZoneId')) {
      return (
        <div className="edit-container">
          <Dropdownlist
            value={editValue}
            items={timeZones}
            labelKey="value"
            valueKey="code"
            placeholder="Select timezone..."
            sortItems={false}
            onItemChange={(item) => handleTimezoneSelect(item)}
            onCancel={() => saveEdit()}
          />
        </div>
      );
    }
    return (
      <div
        className={`editable${isReadOnly ? ' read-only' : ''}`}
        onClick={(e) => { e.stopPropagation(); if (!isReadOnly) startEdit(notification.uuid, 'notificationTimeZoneId', notification.notificationTimeZoneId); }}
      >
        {notification.notificationTimeZoneId || '\u2014'}
      </div>
    );
  };

  const renderTimeCell = (notification: any, field: string) => {
    if (isEditing(notification.uuid, field)) {
      return (
        <div className="edit-container">
          <div
            className="time-picker-control"
            onClick={(e) => openTimePicker(field, e)}
          >
            <span className="time-picker-value">{getTimePickerDisplayValue()}</span>
            <span className="time-picker-caret"></span>
          </div>
        </div>
      );
    }
    return (
      <div
        className={`editable${isReadOnly ? ' read-only' : ''}`}
        onClick={(e) => { e.stopPropagation(); if (!isReadOnly) startEdit(notification.uuid, field, notification[field]); }}
      >
        {formatTime(notification[field])}
      </div>
    );
  };

  const renderMobileFieldEdit = (notification: any, fieldInfo: FieldInfo) => {
    if (fieldInfo.type === 'text' || fieldInfo.type === 'email') {
      return (
        <input
          type={fieldInfo.type}
          className="edit-input"
          value={editValue}
          onChange={(e) => { editValueRef.current = e.target.value; setEditValue(e.target.value); }}
          onBlur={() => saveEdit()}
          onKeyUp={(e) => { if (e.key === 'Enter') saveEdit(); }}
          autoFocus
        />
      );
    }

    if (fieldInfo.type === 'time' || fieldInfo.type === 'time-hour') {
      return (
        <div
          className="time-picker-control"
          onClick={(e) => openTimePicker(fieldInfo.field, e)}
        >
          <span className="time-picker-value">{getTimePickerDisplayValue()}</span>
          <span className="time-picker-caret"></span>
        </div>
      );
    }

    if (fieldInfo.type === 'dropdown') {
      const options = getDropdownOptions(fieldInfo.field);
      return (
        <Dropdownlist
          value={editValue}
          items={options}
          labelKey="value"
          valueKey="code"
          placeholder="Select..."
          searchable={false}
          sortItems={false}
          autoOpen
          onItemChange={(item) => handleDropdownSelect(fieldInfo.field, item)}
          onCancel={() => saveEdit()}
        />
      );
    }

    if (fieldInfo.type === 'timezone') {
      return (
        <Dropdownlist
          value={editValue}
          items={timeZones}
          labelKey="value"
          valueKey="code"
          placeholder="Select timezone..."
          sortItems={false}
          autoOpen
          onItemChange={(item) => handleTimezoneSelect(item)}
          onCancel={() => saveEdit()}
        />
      );
    }

    return null;
  };

  // ---- Time picker portal ----
  const renderTimePicker = () => {
    if (!timePickerOpen) return null;

    const pickerContent = (
      <div className={`time-picker-portal ${theme}`} style={timePickerPortalStyle}>
        {timePickerMode === 'hour-only' ? (
          <div className="time-picker-dropdown">
            <div className="time-picker-list time-picker-list-hour-only">
              {filteredTimeOptions.length === 0 ? (
                <div className="time-picker-item time-picker-item--empty">
                  No times available
                </div>
              ) : (
                filteredTimeOptions.map((option, index) => (
                  <div
                    key={option.value}
                    className={
                      'time-picker-item' +
                      (index === timePickerActiveIndex ? ' time-picker-item--active' : '') +
                      (option.value === editValue ? ' time-picker-item--selected' : '')
                    }
                    onClick={() => selectTimeOption(option)}
                    onMouseEnter={() => setTimePickerActiveIndex(index)}
                  >
                    {option.display}
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="time-picker-dropdown time-picker-multi-column">
            <div className="time-picker-columns">
              {/* Hour Column */}
              <div className="time-picker-column time-picker-column-hour">
                <div className="column-header">Hour</div>
                <div className="column-list">
                  {HOURS_12.map(hour => (
                    <div
                      key={'h' + hour}
                      className={`column-item${hour === selectedHour ? ' column-item--selected' : ''}`}
                      onClick={() => selectHour(hour)}
                    >
                      {hour}
                    </div>
                  ))}
                </div>
              </div>

              {/* Minute Column */}
              <div className="time-picker-column time-picker-column-minute">
                <div className="column-header">Minute</div>
                <div className="column-list">
                  {MINUTES.map(minute => (
                    <div
                      key={'m' + minute}
                      className={`column-item${minute === selectedMinute ? ' column-item--selected' : ''}`}
                      onClick={() => selectMinute(minute)}
                    >
                      {pad(minute)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Period Column */}
              <div className="time-picker-column time-picker-column-period">
                <div className="column-header">Period</div>
                <div className="column-list">
                  {PERIODS.map(period => (
                    <div
                      key={period}
                      className={`column-item${period === selectedPeriod ? ' column-item--selected' : ''}`}
                      onClick={() => selectPeriod(period)}
                    >
                      {period}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );

    return createPortal(pickerContent, document.body);
  };

  // ---- Main render ----
  return (
    <div className={`notifications-assigned ${theme}`} ref={rootRef}>
      <div className="header">
        <h1>{title}</h1>
        {!isReadOnly && (
          <button className="btn-add" onClick={addNotification}>
            <i className="fas fa-plus"></i> NOTIFICATION
          </button>
        )}
      </div>

      {/* Desktop table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              {FIELDS.map(fieldInfo => (
                <th key={fieldInfo.field} style={{ width: HEADER_WIDTHS[fieldInfo.field] || 'auto' }}>
                  {fieldInfo.label}
                </th>
              ))}
              <th style={{ width: '8%' }}>Actions</th>
            </tr>
          </thead>
          {notifications.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={11} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  No notifications configured
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {notifications.map(notification => (
                <tr key={notification.uuid}>
                  {/* Name */}
                  <td>{renderTextCell(notification, 'notifiedName', 'text')}</td>

                  {/* Email */}
                  <td>{renderTextCell(notification, 'notifiedEmail', 'email')}</td>

                  {/* Frequency */}
                  <td>{renderDropdownCell(notification, 'activityReportNotificationFrequency')}</td>

                  {/* Email When No Data */}
                  <td>{renderDropdownCell(notification, 'emailActivityReportWhenNoData')}</td>

                  {/* Report Approved */}
                  <td>{renderDropdownCell(notification, 'receiveReportApprovedNotification')}</td>

                  {/* Call Card Closed */}
                  <td>{renderDropdownCell(notification, 'receiveCallCardClosedNotification')}</td>

                  {/* Timezone */}
                  <td>{renderTimezoneCell(notification)}</td>

                  {/* Notification Time */}
                  <td>{renderTimeCell(notification, 'notificationTimeLocal')}</td>

                  {/* AR Start Time */}
                  <td>{renderTimeCell(notification, 'activityReportStartTimeLocal')}</td>

                  {/* AR End Time */}
                  <td>{renderTimeCell(notification, 'activityReportEndTimeLocal')}</td>

                  {/* Actions */}
                  <td>
                    {!isReadOnly && (
                      <div className="actions">
                        <button className="btn-delete" onClick={() => openDeleteConfirm(notification.uuid)}>
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* Mobile cards */}
      <div className="mobile-cards">
        {notifications.length === 0 ? (
          <div className="empty-state">
            <p>No notifications configured</p>
          </div>
        ) : (
          notifications.map(notification => (
            <div key={'mobile-' + notification.uuid} className="card">
              <div className="card-header">
                <div className="card-header-content">
                  <div className="card-notification-name">
                    {notification.notifiedName || notification.notifiedEmail || 'Notification'}
                  </div>
                </div>
                {!isReadOnly && (
                  <button className="btn-delete" onClick={() => openDeleteConfirm(notification.uuid)}>
                    Delete
                  </button>
                )}
              </div>

              <div className="card-grid">
                {FIELDS.map(fieldInfo => (
                  <div key={fieldInfo.field} className="card-field">
                    <div className="card-field-label">{fieldInfo.label}</div>

                    {isEditing(notification.uuid, fieldInfo.field) ? (
                      <div className="edit-field-mobile">
                        {renderMobileFieldEdit(notification, fieldInfo)}
                      </div>
                    ) : (
                      <div
                        className={`card-field-value${isReadOnly ? ' read-only' : ''}`}
                        onClick={() => {
                          if (!isReadOnly) {
                            startEditMobile(notification.uuid, fieldInfo.field, notification[fieldInfo.field]);
                          }
                        }}
                      >
                        {getDisplayValue(notification, fieldInfo.field)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete confirmation modal */}
      <ConfirmModal
        open={deleteConfirm.open}
        title={'Remove ' + deleteConfirm.name + '?'}
        confirmText="Remove"
        cancelText="Cancel"
        autoFocus="cancel"
        scrollLock={true}
        theme={theme}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      {/* Time picker portal */}
      {renderTimePicker()}
    </div>
  );
}

(window as any).ClientNotificationsGridReact = ClientNotificationsGrid;
